import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import BillDropzone from './Global/BillDropzone';

const API =
	typeof window !== 'undefined' && window.location.href.includes('localhost')
		? 'http://localhost:5053/api'
		: 'https://solargabe.com/api';

export default function BillUploadPage() {
	const { customerId, token } = useParams();
	const [state, setState] = useState('checking'); // checking | ready | invalid
	const [name, setName] = useState(null);
	const [file, setFile] = useState(null);
	const [submitting, setSubmitting] = useState(false);
	const [done, setDone] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		fetch(`${API}/upload/public/${customerId}/${token}`)
			.then((r) => r.json())
			.then((j) => {
				if (j.ok) {
					setName(j.name);
					setState('ready');
				} else {
					setState('invalid');
				}
			})
			.catch(() => setState('invalid'));
	}, [customerId, token]);

	const submit = async () => {
		if (!file) return;
		setSubmitting(true);
		setError(null);
		try {
			const fd = new FormData();
			fd.append('bill', file);
			const res = await fetch(`${API}/upload/public/${customerId}/${token}`, {
				method: 'POST',
				body: fd,
			});
			const j = await res.json();
			if (res.ok && j.ok) {
				setDone(true);
			} else {
				setError(j.error || 'Upload failed. Please try again.');
			}
		} catch {
			setError('Upload failed. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className='flex min-h-[70vh] items-center justify-center bg-surface px-6 py-16'>
			<div className='w-full max-w-lg'>
				<div className='rounded-3xl bg-white p-8 shadow-xl shadow-ink-900/5 ring-1 ring-ink-100 sm:p-10'>
					{state === 'checking' && (
						<div className='flex justify-center py-10'>
							<div className='h-8 w-8 animate-spin rounded-full border-2 border-primary-500 border-t-transparent' />
						</div>
					)}

					{state === 'invalid' && (
						<div className='text-center'>
							<h1 className='font-display text-2xl font-medium text-ink-900'>
								This link isn&apos;t valid
							</h1>
							<p className='mt-3 text-sm text-ink-500'>
								The upload link may have expired or been mistyped. Reach out and
								we&apos;ll send a fresh one.
							</p>
						</div>
					)}

					{state === 'ready' && !done && (
						<>
							<p className='text-xs font-semibold uppercase tracking-[0.18em] text-primary-600'>
								Solar Gabe
							</p>
							<h1 className='font-display mt-3 text-2xl font-medium text-ink-900'>
								{name ? `${name}, upload your utility bill` : 'Upload your utility bill'}
							</h1>
							<p className='mt-3 text-sm leading-6 text-ink-500'>
								This helps us size your system and give you an accurate savings
								estimate. A recent bill (PDF or a clear photo) is perfect.
							</p>
							<div className='mt-6'>
								<BillDropzone file={file} onFile={setFile} />
							</div>
							{error && <p className='mt-3 text-sm text-rose-500'>{error}</p>}
							<button
								onClick={submit}
								disabled={!file || submitting}
								className='mt-6 w-full rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-primary-500/20 hover:bg-primary-600 disabled:opacity-50 transition-all'
							>
								{submitting ? 'Uploading…' : 'Send my bill'}
							</button>
						</>
					)}

					{done && (
						<div className='text-center'>
							<CheckCircleIcon className='mx-auto h-14 w-14 text-primary-500' />
							<h1 className='font-display mt-4 text-2xl font-medium text-ink-900'>
								Got it — thank you!
							</h1>
							<p className='mt-3 text-sm text-ink-500'>
								Your utility bill is on its way to {name ? 'your' : 'the'} project
								file. We&apos;ll be in touch shortly.
							</p>
							<button
								onClick={() => {
									setFile(null);
									setDone(false);
								}}
								className='mt-6 text-sm font-semibold text-primary-600 hover:text-primary-700'
							>
								Upload another file
							</button>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
